import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const AUTH_BASE_URL = (process.env.REACT_APP_AUTH_URL || 'https://auth.wattswap.ai').replace(/\/$/, '');

function roleFromAuthLevel(authLevel) {
    if (authLevel >= 5) return 'admin';
    if (authLevel >= 1) return 'viewer';
    return 'guest';
}

function normalizeUser(profile) {
    if (!profile) return null;
    const authLevel = profile.auth_level != null ? profile.auth_level : 1;
    return {
        user_id: profile.user_id,
        email: profile.email,
        username: profile.username,
        auth_level: authLevel,
        role: roleFromAuthLevel(authLevel),
    };
}

class AuthService {
    constructor() {
        this.token = localStorage.getItem('auth_token');
        this.user = null;

        axios.interceptors.request.use((config) => {
            if (this.token) {
                config.headers.Authorization = `Bearer ${this.token}`;
            }
            return config;
        });

        this._onUnauthorized = null;
        axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    this.clearSession();
                    if (this._onUnauthorized) this._onUnauthorized();
                }
                return Promise.reject(error);
            }
        );
    }

    async login(credentials) {
        try {
            const isToken = typeof credentials === 'string';
            if (isToken) {
                const response = await axios.post(`${API_BASE_URL}/auth/validate`, { token: credentials });
                if (response.data.success) {
                    this.token = credentials;
                    this.user = normalizeUser(response.data.data.user);
                    localStorage.setItem('auth_token', credentials);
                    return { success: true, user: this.user };
                }
                throw new Error(response.data.error || 'Invalid token');
            }

            const { email, password } = credentials || {};
            if (!email || !password) {
                return { success: false, error: 'Email and password are required' };
            }

            const loginRes = await axios.post(
                `${AUTH_BASE_URL}/api/auth/lean/login`,
                { email: email.trim(), password, device_info: {} },
                { timeout: 10000, headers: { 'Content-Type': 'application/json' } }
            );

            const data = loginRes.data;
            if (!data || !data.success) {
                throw new Error(data?.detail || data?.error_message || 'Login failed');
            }

            // MFA required — return challenge so the UI can show the OTP step.
            if (data.mfa_required && data.mfa_challenge_token) {
                return {
                    success: false,
                    mfa_required: true,
                    mfa_challenge_token: data.mfa_challenge_token,
                    email_hint: data.email_hint || '',
                    message: data.message || 'Enter the verification code sent to your email',
                    // Present only when auth ENV=development (email not sent)
                    dev_otp: data.dev_otp || null,
                };
            }

            if (!data.session_token) {
                throw new Error(data?.detail || data?.error_message || 'Login failed');
            }

            return this._applySession(data);
        } catch (error) {
            const msg =
                error.response?.data?.detail ||
                (Array.isArray(error.response?.data?.detail) ? error.response?.data?.detail?.[0] : null) ||
                error.response?.data?.error_message ||
                error.message;
            return { success: false, error: msg || 'Login failed' };
        }
    }

    async verifyMfa(challengeToken, otp) {
        try {
            const res = await axios.post(
                `${AUTH_BASE_URL}/api/auth/lean/login/verify-mfa`,
                { mfa_challenge_token: challengeToken, otp: String(otp).trim() },
                { timeout: 15000, headers: { 'Content-Type': 'application/json' } }
            );
            const data = res.data;
            if (!data || !data.success || !data.session_token) {
                throw new Error(data?.detail || data?.error_message || 'Verification failed');
            }
            return this._applySession(data);
        } catch (error) {
            const msg =
                error.response?.data?.detail ||
                (Array.isArray(error.response?.data?.detail) ? error.response?.data?.detail?.[0] : null) ||
                error.response?.data?.error_message ||
                error.message;
            return { success: false, error: msg || 'Verification failed' };
        }
    }

    _applySession(data) {
        const token = data.session_token;
        const profile = data.user_profile || {};
        this.token = token;
        this.user = normalizeUser(profile);
        localStorage.setItem('auth_token', token);
        return { success: true, user: this.user };
    }

    clearSession() {
        this.token = null;
        this.user = null;
        localStorage.removeItem('auth_token');
    }

    setOnUnauthorized(callback) {
        this._onUnauthorized = callback;
    }

    logout() {
        this.clearSession();
        window.location.href = '/login';
    }

    async getCurrentUser() {
        if (!this.token) return { success: false, error: 'No token available' };
        try {
            const response = await axios.get(`${API_BASE_URL}/auth/me`);
            if (response.data.success) {
                this.user = normalizeUser(response.data.data.user);
                return { success: true, user: this.user };
            }
            throw new Error(response.data.error || 'Failed to get user');
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.error || error.message,
                status: error.response?.status,
            };
        }
    }

    async checkPermission(permission) {
        if (!this.token) return false;
        try {
            const response = await axios.get(`${API_BASE_URL}/auth/permission/${permission}`);
            return response.data.data?.granted ?? false;
        } catch {
            return false;
        }
    }

    isAuthenticated() {
        return !!this.token;
    }

    isAdmin() {
        return this.user?.auth_level >= 5 || this.user?.role === 'admin';
    }

    isViewer() {
        return this.user?.auth_level >= 1 || this.user?.role === 'viewer' || this.isAdmin();
    }

    getUser() {
        return this.user;
    }

    getToken() {
        return this.token;
    }

    hasPermission(permission) {
        if (!this.user) return false;
        if (this.isAdmin()) return true;
        switch (permission) {
            case 'firmware:read':
                return this.isViewer();
            case 'firmware:write':
            case 'firmware:delete':
            case 'device:write':
            case 'device:delete':
                return this.isAdmin();
            default:
                return false;
        }
    }
}

const authService = new AuthService();
export default authService;
